import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { fetchProducts, getImageUrl } from '../api/api';
import api from '../api/api';
import { useCurrency } from '../context/CurrencyContext';
import '../pages/Home.css';
import './ProductCard.css';
import './Products.css';

const FALLBACK_CATEGORIES = [
  { key: 'all',                label: 'All Products' },
  { key: 'electric-scooters', label: 'Electric Scooters' },
  { key: 'electric-bikes',    label: 'Electric Bikes' },
  { key: 'spare-parts',      label: 'Spare Parts' },
];

function ProductCard({ product }) {
  const images = product.images?.length ? product.images : [product.image].filter(Boolean);
  const displayImages = images.slice(0, 3); // Limit to 3 images
  const [activeImg, setActiveImg] = useState(0);
  const [hovered, setHovered] = useState(false);
  const currency = useCurrency();
  
  const price = Number(product.price || 0);
  const oldPrice = Number(product.oldPrice || 0);
  const hasDiscount = oldPrice > 0 && oldPrice > price;
  const isSparepart = product.category === 'spare-parts';
  const subCategoryLabel = product.subCategory ? product.subCategory.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

  return (
    <div
      className={`home-product-card${hovered ? ' is-hovered' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="home-product-img">
        <img
          src={getImageUrl(images[activeImg])}
          alt={product.name}
          onError={e => { e.target.src = '/images/scooter-1.png'; }}
        />

        <div className="img-thumbnails">
          {displayImages.map((img, i) => (
            <button
              key={i}
              className={`thumb${activeImg === i ? ' active' : ''}`}
              onClick={() => setActiveImg(i)}
            >
              <img src={getImageUrl(img)} alt="" onError={e => { e.target.src = '/images/scooter-1.png'; }} />
            </button>
          ))}
        </div>
      </div>

      <div className="home-product-info">
        <div className="prd-rating">
          {'★★★★★'.split('').map((s, i) => <span key={i} className="star">{s}</span>)}
        </div>
        <span className="home-range">{product.range || ''}</span>
        <h3>{product.name.replace(/ Electric Scooter| Electric Bike| Scooter| Bike/gi, '').trim()}</h3>
        <div className="home-product-footer">
          <div className="home-price-wrap">
            {hasDiscount && <div className="price-old">{currency}{oldPrice.toFixed(2)}</div>}
            <div className="home-price">{currency}{price.toFixed(2)}</div>
          </div>
          <Link
            to={`/products/${product.category}/${product.model}`}
            className={`btn buy-btn ${product.inStock === false ? 'btn-soldout' : ''}`}
          >
            {product.inStock === false ? 'SOLD OUT' : 'BUY NOW'}
          </Link>
        </div>
      </div>
    </div>
  );
}

function Products() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  const urlCategory = params.get('category') || 'all';
  const urlModel    = params.get('model')    || '';
  const urlBrand    = params.get('brand')    || '';
  const urlFor      = params.get('for')      || '';

  const [products, setProducts] = useState([]);
  const [parentProducts, setParentProducts] = useState([]);
  const [forProductName, setForProductName] = useState('');
  const [loading, setLoading]   = useState(true);
  const [activeCategory, setActiveCategory] = useState(urlCategory); // eslint-disable-line no-unused-vars
  const [sortBy, setSortBy] = useState('price-asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  useEffect(() => {
    api.get('/categories').then(({ data }) => {
      if (data?.length) setCategories(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setActiveCategory(p.get('category') || 'all');
  }, [location.search]);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams(location.search);
    const cat = p.get('category') || 'all';
    const model = p.get('model') || '';
    const brand = p.get('brand') || '';
    const forId = p.get('for') || '';

    const query = {};
    if (cat !== 'all') query.category = cat;
    if (model) query.model = model;
    if (brand) query.brand = brand;
    if (forId) query.for = forId;

    fetchProducts(query)
      .then(data => {
        if (model && data.length === 1 && data[0].model) {
          navigate(`/products/${data[0].category}/${data[0].model}`, { replace: true });
          return;
        }
        // Exclude spare parts from default "all" view
        if (cat === 'all') {
          setProducts(data.filter(p => p.category === 'electric-scooters' || p.category === 'electric-bikes'));
        } else {
          setProducts(data);
        }
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));

    // For spare-parts without a specific product filter, load parent products for the grid
    if (cat === 'spare-parts' && !forId && !brand) {
      fetchProducts().then(all => {
        const scooters = all.filter(p => p.category === 'electric-scooters');
        const bikes = all.filter(p => p.category === 'electric-bikes');
        setParentProducts([...scooters, ...bikes]);
      }).catch(() => {});
    } else {
      setParentProducts([]);
    }

    // If filtering by product, fetch the product name for the title
    if (forId) {
      fetchProducts().then(all => {
        const prod = all.find(p => (p._id || p.id) === forId);
        if (prod) setForProductName(prod.name);
      }).catch(() => {});
    } else {
      setForProductName('');
    }
  }, [location.search, navigate]);

  const handleCategory = (key) => {
    setActiveCategory(key);
    navigate(key === 'all' ? '/products' : `/products?category=${key}`);
  };

  const currentCat = new URLSearchParams(location.search).get('category') || 'all';
  
  const pageTitle = urlModel
    ? products[0]?.name || 'Product'
    : urlFor && forProductName
    ? `Spare Parts for ${forProductName}`
    : urlBrand
    ? currentCat === 'spare-parts'
      ? `Spare Parts for ${urlBrand.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`
      : `${urlBrand.replace(/-/g, ' ')} Parts`
    : categories.find(c => c.key === currentCat)?.label || 'All Products';

  // Show product grid selector for spare-parts landing page (no filter applied)
  const isSparePartsLanding = false;

  return (
    <div className="products-page section">
      <div className="container">
        <h1>{pageTitle}</h1>

        {!urlModel && !urlBrand && !urlFor && (
          <div className="filter-bar">
            {categories.map(cat => (
              <button
                key={cat.key}
                className={currentCat === cat.key ? 'active' : ''}
                onClick={() => handleCategory(cat.key)}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <input
              type="text"
              placeholder={currentCat === 'spare-parts' ? "Search spare parts..." : "Search electric scooters & bikes..."}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ width: '100%', maxWidth: 400, padding: '12px 16px', border: '1.5px solid #ddd', borderRadius: 30, fontSize: 14, outline: 'none', transition: 'border 0.2s' }}
              onFocus={e => e.target.style.borderColor = '#00d084'}
              onBlur={e => e.target.style.borderColor = '#ddd'}
            />
          </div>

        {(urlModel || urlBrand || urlFor) && (
          <button className="btn-back" onClick={() => navigate(currentCat === 'spare-parts' ? '/products?category=spare-parts' : '/products')}>
            ← Back to {currentCat === 'spare-parts' ? 'All Spare Parts' : 'All Products'}
          </button>
        )}

        {/* Spare Parts landing: show product grid to select which product's parts to view */}
        {isSparePartsLanding && (
          <div style={{ marginBottom: 32 }}>
            <p style={{ color: '#555', fontSize: 15, marginBottom: 16 }}>Select a product to view its available spare parts:</p>
            {parentProducts.length > 0 ? (
              <div className="home-products-grid">
                {parentProducts.map(product => (
                  <div
                    key={product._id}
                    className="home-product-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/products?category=spare-parts&for=${product._id}`)}
                  >
                    <div className="home-product-img">
                      <img
                        src={getImageUrl(product.images?.[0] || product.image)}
                        alt={product.name}
                        onError={e => { e.target.src = '/images/scooter-1.png'; }}
                      />
                      <div style={{ position: 'absolute', top: 8, right: 8, background: product.category === 'electric-scooters' ? '#00d084' : '#1a73e8', color: '#fff', padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
                        {product.category === 'electric-scooters' ? 'Scooter' : 'Bike'}
                      </div>
                    </div>
                    <div className="home-product-info">
                      <h3>{product.name}</h3>
                      <div style={{ fontSize: 12, color: '#00d084', fontWeight: 600 }}>View Spare Parts →</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="loading">Loading products...</div>
            )}
          </div>
        )}

        {/* Show products/spare parts grid (not on spare-parts landing) */}
        {!isSparePartsLanding && (
          <>
            {!loading && products.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button onClick={() => setSortBy(sortBy === 'price-asc' ? 'price-desc' : 'price-asc')} style={{ padding: '9px 18px', border: '2px solid #2ecc71', borderRadius: 25, fontSize: 13, fontWeight: 600, background: '#fff', color: '#2ecc71', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '6px' }}>{sortBy === 'price-asc' ? '↑ Price: Low to High' : '↓ Price: High to Low'}</button>
              </div>
            )}
            {loading ? (
              <div className="loading">Loading products...</div>
            ) : products.length === 0 ? (
              <div className="no-products">{urlFor ? 'No spare parts linked to this product yet.' : 'No products found.'}</div>
            ) : (
              <div className="home-products-grid">
                {[...products]
                  .filter(p => {
                    if (!searchQuery.trim()) return true;
                    const q = searchQuery.toLowerCase();
                    return p.name?.toLowerCase().includes(q);
                  })
                  .sort((a, b) => {
                  if (sortBy === 'name-asc') return (a.name || '').localeCompare(b.name || '');
                  if (sortBy === 'name-desc') return (b.name || '').localeCompare(a.name || '');
                  if (sortBy === 'price-asc') return (a.price || 0) - (b.price || 0);
                  if (sortBy === 'price-desc') return (b.price || 0) - (a.price || 0);
                  if (sortBy === 'date-new') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                  if (sortBy === 'date-old') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                  return 0;
                }).map(product => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Products;
